/**
 * Set a free price-data API key from inside the app.
 *
 * The free scraped sources (Yahoo, Stooq, Nasdaq) periodically block automated
 * requests. A free API key is the reliable, fully-automatic fix — and setting
 * it here means no editing of the hidden .env file. Once saved, every sync
 * uses it automatically.
 */

import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/api/client";

interface KeyStatus {
  twelve_data: boolean;
  alpha_vantage: boolean;
  signup_urls: { twelve_data: string; alpha_vantage: string };
}

export default function ApiKeyPanel({
  onSaved,
}: {
  onSaved?: () => void;
}): React.ReactElement {
  const [status, setStatus] = useState<KeyStatus | null>(null);
  const [keyValue, setKeyValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async (): Promise<void> => {
    try {
      const { data } = await apiClient.get<KeyStatus>("/providers/keys");
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const save = async (): Promise<void> => {
    const trimmed = keyValue.trim();
    if (!trimmed) {
      setError("Paste your API key first.");
      return;
    }
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await apiClient.post<{ message: string }>("/providers/keys", {
        provider: "twelve_data",
        key: trimmed,
      });
      setMessage(`${data.message} Now sync a ticker or use “Load major stocks”.`);
      setKeyValue("");
      await loadStatus();
      onSaved?.();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ? String(detail) : "Couldn't save the key.");
    } finally {
      setIsSaving(false);
    }
  };

  const configured = status?.twelve_data || status?.alpha_vantage;
  const signupUrl = status?.signup_urls.twelve_data ?? "https://twelvedata.com/pricing";

  return (
    <div className="panel border-l-2 border-l-signal-teal p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-mist-100">
          Automatic prices with a free API key
        </h2>
        {configured && (
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300">
            key configured
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-mist-400">
        The most reliable fix. Get a free key (no card needed), paste it here, and every ticker
        loads prices automatically — no files, no downloads.
      </p>

      <ol className="mt-3 space-y-1 text-sm text-mist-300">
        <li>
          1. Open{" "}
          <a
            href={signupUrl}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-signal-teal hover:underline"
          >
            twelvedata.com
          </a>{" "}
          → sign up free → copy your API key.
        </li>
        <li>2. Paste it below and save.</li>
      </ol>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={keyValue}
          onChange={(event) => setKeyValue(event.target.value)}
          placeholder="Paste your Twelve Data API key"
          className="w-72 rounded-lg border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-mist-100 placeholder:text-mist-500 focus:border-ink-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={isSaving}
          className="rounded-lg border border-signal-teal/40 bg-signal-teal/10 px-4 py-2 text-sm font-medium text-signal-teal transition hover:bg-signal-teal/20 disabled:opacity-60"
        >
          {isSaving ? "Saving…" : "Save key"}
        </button>
      </div>

      {message && <p className="mt-3 text-sm text-emerald-400">{message}</p>}
      {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
    </div>
  );
}
